import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaClock,
  FaChalkboardTeacher,
  FaSearch,
  FaFilter,
  FaUsers,
} from "react-icons/fa";
import { courseAPI } from "../services/postgresService";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactPopup from "../components/ContactPopup";
import ParallaxHeroSection from "../components/ParallaxHeroSection";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";

const CourseCard = ({ course, index, onEnrollClick, t }) => {
  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{
        y: -10,
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
    >
      <div className="relative">
        <img
          src={course.image}
          alt={course.title}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-0 right-0 bg-safir-primary text-white px-3 py-1 m-2 rounded-full text-sm font-semibold">
          {course.category}
        </div>
      </div>

      <div className="p-6 flex-grow">
        <h3 className="text-xl font-bold text-safir-dark mb-2">
          {course.title}
        </h3>
        <p className="text-gray-600 mb-4">{course.description}</p>

        <div className="flex flex-wrap gap-4 text-gray-500 text-sm mb-4">
          <div className="flex items-center">
            <FaUsers className="mr-2 text-safir-primary" />
            <span>{course.students || 0} {t('courses.students')}</span>
          </div>
          <div className="flex items-center">
            <FaClock className="mr-2 text-safir-primary" />
            <span>{course.duration}</span>
          </div>
          <div className="flex items-center">
            <FaChalkboardTeacher className="mr-2 text-safir-primary" />
            <span>{course.instructor}</span>
          </div>
          {course.start_date && (
            <div className="flex items-center">
              <span className="mr-2 text-safir-primary">Starts:</span>
              <span>{new Date(course.start_date).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 pb-6">
        <motion.button
          className="w-full bg-safir-primary hover:bg-safir-secondary text-white font-semibold py-2 px-4 rounded-md transition-colors duration-300"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={onEnrollClick}
        >
          {t('courses.enrollNow')}
        </motion.button>
      </div>
    </motion.div>
  );
};

const CoursesPage = () => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showContactPopup, setShowContactPopup] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);

    const fetchCourses = async () => {
      try {
        const data = await courseAPI.getAll();
        setCourses(data);
        setFilteredCourses(data);

        // Extract unique categories
        const uniqueCategories = [
          ...new Set(data.map((course) => course.category)),
        ];
        setCategories(uniqueCategories);

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching courses:", error);
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    // Filter courses based on search term and category
    const filtered = courses.filter((course) => {
      const matchesSearch =
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.instructor.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === "" || course.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    setFilteredCourses(filtered);
  }, [searchTerm, selectedCategory, courses]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Courses - Professional Learning Programs"
        description="Explore our wide range of professional courses at Safir Elite. From technology to business, find the perfect course to advance your career and skills."
        canonical="/courses"
        ogType="website"
        keywords="courses, professional training, Afghanistan, Kabul, online courses, education, learning"
      />
      <StructuredData 
        type="WebSite" 
        data={{ 
          name: 'Safir Elite Courses',
          url: 'https://safir-foundation.org/courses'
        }} 
      />
      <Navbar />

      {/* Hero Section */}
      <ParallaxHeroSection
        backgroundKey="courses"
        title={t('courses.title')}
        subtitle={t('courses.description')}
      >
        <motion.div
          className="w-24 h-1 bg-safir-primary mx-auto mt-4"
          initial={{ width: 0 }}
          animate={{ width: 96 }}
          transition={{ duration: 0.8 }}
        ></motion.div>
      </ParallaxHeroSection>

      {/* Filters Section */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 -mt-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('courses.searchPlaceholder')}
                className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaFilter className="text-gray-400" />
              </div>
              <select
                className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary appearance-none"
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <option value="">{t('courses.all')}</option>
                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-safir-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('courses.loading')}</p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">
              {t('courses.noCoursesFound')}
            </p>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredCourses.map((course, index) => (
              <CourseCard
                key={course.id}
                course={course}
                index={index}
                onEnrollClick={() => setShowContactPopup(true)}
                t={t}
              />
            ))}
          </motion.div>
        )}
      </div>

      <Footer />
      <ContactPopup
        isOpen={showContactPopup}
        onClose={() => setShowContactPopup(false)}
      />
    </div>
  );
};

export default CoursesPage;
